import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  MOBILE_MODAL_ANIMATION_DURATION,
  MODAL_ANIMATION_DURATION,
} from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Modal, Textarea } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';

import styles from './ClinicalCaseDescriptionEditorModal.module.scss';

export const ClinicalCaseDescriptionEditorModal = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [newDescription, setNewDescription] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement>(
    null,
  ) as React.MutableRefObject<HTMLTextAreaElement>;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const { visible, data } = useAppSelector(
    modalModel.selectors.selectClinicalCaseDescriptionEditorModal,
  );
  const { patientID } = data;

  const currentDescription = useAppSelector(
    patientModel.selectors.selectClinicalCaseDescription,
  );

  const closeModal = () => {
    dispatch(
      modalModel.actions.closeModal(ModalID.ClinicalCaseDescriptionEditorModal),
    );
  };

  const handleSave = async () => {
    trackEvent({
      eventName: 'ReportPageClinicalCaseDescriptionSaveClicked',
    });

    if (newDescription !== currentDescription) {
      setIsLoading(true);

      try {
        await dispatch(
          patientModel.thunks.setPatientClinicalCaseDescription({
            PatientID: patientID,
            ClinicalCaseDescription: newDescription,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    }

    closeModal();
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;

    if (!isMobile && textarea) {
      textarea.style.height = 'auto';
      const maxHeight = window.innerHeight * 0.7;
      const contentHeight = textarea.scrollHeight;

      if (contentHeight > maxHeight) {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = `${contentHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
    }
  };

  useEffect(() => {
    if (visible && textareaRef.current) {
      setNewDescription(currentDescription ?? '');

      // HACK: This is a hack to ensure the textarea is focused after the modal is opened
      const timer = setTimeout(
        () => {
          textareaRef.current?.focus();

          adjustTextareaHeight();
        },
        isMobile ? MOBILE_MODAL_ANIMATION_DURATION : MODAL_ANIMATION_DURATION,
      );

      return () => clearTimeout(timer);
    }
  }, [visible]);

  return (
    <Modal
      data-component="clinical-case-description-editor"
      containerClassName={styles.container}
      title={formatMessage({
        id: 'patientInfo.clinicalCaseDescription',
        defaultMessage: 'Clinical case description',
      })}
      isOpen={visible}
      onCancel={closeModal}
      borderless
      onOk={handleSave}
      okButtonText={formatMessage({
        id: 'global.save',
        defaultMessage: 'Save',
      })}
      okButtonProps={{
        variant: 'secondary',
        loading: isLoading,
      }}
      cancelButtonProps={{
        variant: 'tertiary',
      }}
      shouldRenderCancelButton={!isMobile}
      shouldCloseOnOverlayClick={false}
    >
      <Textarea
        isElastic={isMobile}
        outerRef={textareaRef}
        value={newDescription}
        onChange={(event) => {
          setNewDescription(event.target.value);

          adjustTextareaHeight();
        }}
        className={styles.textarea}
      />
    </Modal>
  );
};
